import { Box, TextField } from '@mui/material';
import { CalendarLineIcon } from '~/assets/icons';
import { forwardRef, InputHTMLAttributes, memo, useEffect, useState } from 'react';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './BaseDatePicker.module.scss';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import DatePicker, { registerLocale } from 'react-datepicker';
import { vi, enUS } from 'date-fns/locale'; // Import locale Tiếng Việt
import { useTranslation } from 'react-i18next';

registerLocale('vi', vi);
registerLocale('en', enUS);

const cx = classNames.bind(styles);

export type BaseDatePickerProps = {
  value?: Date | string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  FormHelperTextProps?: any;
  dateFormat?: string;
  portalId?: string;
  customInput?: JSX.Element;
  disabled?: boolean;
  className?: string;
  style?: any;
  label?: string;
  onChange?: (date: string | null) => void;
  showYearPicker?: boolean;
  showMonthYearPicker?: boolean;
  showTimeSelect?: boolean;
  calendarProps?: any;
  uiVariant?: 'default' | 'calendarPicker';
};

type TextFieldInputProps = InputHTMLAttributes<HTMLInputElement>;

export const BaseDatePicker = (props: BaseDatePickerProps) => {
  const { i18n } = useTranslation();

  //#region Destructuring Props
  const {
    value,
    placeholder,
    error,
    helperText,
    FormHelperTextProps,
    dateFormat = 'ddd, MMM DD, YYYY',
    portalId,
    label,
    showYearPicker = false,
    showMonthYearPicker = false,
    showTimeSelect = false,
    calendarProps = {},
    uiVariant = 'default',
  } = props;
  //#endregion Destructuring Props

  //#region Declare State
  const [, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());

  const [isDateInFuture, setIsDateInFuture] = useState(true);

  const [dateText, setDateText] = useState<string>('');
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (value) {
      setStartDate(dayjs(value).toDate());
      setIsDateInFuture(dayjs(value).isAfter(startDate));
      setDateText(dayjs(value).format(dateFormat));
    } else {
      setStartDate(undefined);
      setDateText('');
      setIsDateInFuture(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  //#endregion Implement Hook

  //#region Handle Function
  const onChange = (date: Date | null) => {
    if (date) {
      setStartDate(date);
      setDateText(dayjs(date).format(dateFormat));
    } else {
      setStartDate(undefined);
    }
    if (props.onChange) props.onChange(date ? dayjs(date).format('YYYY-MM-DD') : null);

    setIsDateInFuture(date ? dayjs(date).isAfter(dayjs()) : false);
    setIsOpen(false);
  };
  //#endregion Handle Function

  const TextFieldInput = forwardRef<HTMLInputElement, TextFieldInputProps>(({ onClick }, ref) => (
    <TextField
      ref={ref}
      inputProps={{ readOnly: true }}
      InputProps={{
        endAdornment: <CalendarLineIcon className='w-[18px]' />,
      }}
      onClick={onClick}
      value={dateText}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      FormHelperTextProps={FormHelperTextProps}
      className={cx(props.className, {
        'calendar-picker-field': uiVariant === 'calendarPicker',
      })}
      style={props.style}
      label={label}
      sx={{
        '& input::placeholder': {
          color: '#7C8796',
          fontWeight: '200',
          opacity: 1,
        },
      }}
    />
  ));

  return (
    <Box className={cx('base-date-picker-container')}>
      <Box className={cx('base-date-picker-menu', { 'date-in-future': isDateInFuture })}>
        <DatePicker
          portalId={portalId}
          popperProps={{ strategy: 'fixed' }}
          selectsMultiple={undefined}
          onChange={onChange}
          selected={startDate}
          dateFormat={dateFormat}
          calendarStartDay={1}
          showPopperArrow={false}
          disabled={props.disabled ?? false}
          locale={i18n.language}
          customInput={
            props?.customInput || (
              <TextFieldInput
                value={dateText}
                onClick={() => {
                  setIsOpen(true);
                }}
                readOnly
              />
            )
          }
          onClickOutside={() => {
            setIsOpen(false);
          }}
          showMonthYearPicker={showMonthYearPicker}
          showYearPicker={showYearPicker}
          showTimeSelect={showTimeSelect}
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode='select'
          {...calendarProps}
        />
      </Box>
    </Box>
  );
};

export default memo(BaseDatePicker);
